import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactNode, createContext, useCallback, useContext } from 'react';

import { PlayerId, SchoolId, TeamId } from '@suntex.fim/api-client';
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react';
import { TeamTopPlayersType } from '@suntex.fim/enums';
import { useFramework } from '@suntex.fim/framework';
import { LoaderOverlay } from '@suntex.fim/ui';

import { isValidPlayerId } from '../utils';

import { PREVIEW_PROGRESS_SUMMARY } from './previewData';
import { PlayerContext as IPlayerContext, PlayerIndexResponse } from './types';

const PlayerContext = createContext<IPlayerContext | null>(null);

/**
 * Hook to access the PlayerContext.
 * Must be used within a PlayerProvider.
 * @throws Error if used outside of PlayerProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePlayerContext(): IPlayerContext {
  const context = useContext(PlayerContext);
  if (context === null) {
    throw new Error('usePlayerContext must be used within a PlayerProvider');
  }
  return context;
}

/** Returns the PlayerContext value, or `null` when no PlayerProvider is present. */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalPlayerContext(): IPlayerContext | null {
  return useContext(PlayerContext);
}

export interface PlayerProviderProps {
  children: ReactNode;
  /** Optional override for preview mode. If not provided, derived from !isValidPlayerId(playerId) */
  isPreview?: boolean;
  /** Optional override for player ID. If not provided, uses login.playerId */
  playerId?: PlayerId;
  /** Optional override for team ID. If not provided, uses login.teamId */
  teamId?: TeamId;
}

interface PlayerProviderCoreProps {
  children: ReactNode;
  playerId: PlayerId;
  teamId: TeamId;
  isPreview: boolean;
}

function PlayerProviderCore({
  children,
  playerId,
  teamId,
  isPreview,
}: PlayerProviderCoreProps) {
  const { api } = useFramework();
  const { login } = useAuth(isAuthenticated);
  const queryClient = useQueryClient();

  const {
    data: infoResult,
    error: infoError,
    isLoading: isInfoLoading,
  } = useQuery({
    queryKey: ['player', 'info', playerId, teamId, isPreview],
    queryFn: async () => {
      if (isPreview)
        return {
          info: {
            team: {
              id: 0 as TeamId,
            },
            school: {
              id: 0 as SchoolId,
            },
            isFamily: false,
            isTeacher: false,
          },
          playerInfo: null,
        };
      const playerInfo = (await api.get_Player_Index({
        playerId,
        teamId,
        loginId: login.id,
        loginType: login.loginType,
        type: TeamTopPlayersType.AllTime,
      })) as PlayerIndexResponse;

      return {
        info: {
          avatar: {
            badgeIcon: playerInfo.player.badgeIcon,
            badgeBackground: playerInfo.player.badgeBackground,
          },
          team: {
            id: playerInfo.team?.teamId || (0 as TeamId),
            name: playerInfo.team?.teamName || '',
          },
          school: {
            id: playerInfo.team?.schoolId || (0 as SchoolId),
            name: playerInfo.team?.schoolName || '',
          },
          userId: playerInfo.player.userId,
          isFamily: Boolean(playerInfo.player.isFamily),
          isTeacher: Boolean(playerInfo.player.isTeacher),
        },
        playerInfo,
      };
    },
  });

  const {
    data: progressSummary,
    error: progressSummaryError,
    isLoading: isProgressSummaryLoading,
  } = useQuery({
    queryKey: ['player', 'progressSummary', playerId, isPreview],
    queryFn: async () => {
      if (isPreview) return PREVIEW_PROGRESS_SUMMARY;
      return await api.get_Player_ProgressSummary({ playerId });
    },
  });

  const reload = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['player', 'info', playerId],
      }),
      queryClient.invalidateQueries({
        queryKey: ['player', 'progressSummary', playerId],
      }),
    ]);
  }, [queryClient, playerId]);

  if (isInfoLoading || isProgressSummaryLoading) {
    return <LoaderOverlay isLoading />;
  }

  // previous fallback was actually less helpful than the default error page
  if (infoError) throw infoError;
  if (progressSummaryError) throw progressSummaryError;

  if (!infoResult || !progressSummary) {
    return null;
  }

  const value: IPlayerContext = {
    playerId,
    isPreview,
    info: infoResult.info,
    playerInfo: infoResult.playerInfo,
    progressSummary,
    reload,
  };

  return (
    <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
  );
}

/**
 * Provider component that fetches and provides player data to all children.
 * Handles loading and error states internally. Automatically resets when the
 * resolved player ID changes, so consumers never see stale data from a
 * previous entity.
 */
export function PlayerProvider({
  children,
  isPreview: isPreviewProp,
  playerId: playerIdProp,
  teamId: teamIdProp,
}: PlayerProviderProps) {
  const { login } = useAuth(isAuthenticated);

  // Use props when provided, otherwise fall back to auth-derived values
  const playerId = playerIdProp ?? login.playerId;
  const teamId = teamIdProp ?? login.teamId;
  const isPreview = isPreviewProp ?? !isValidPlayerId(playerId);

  // Guard against invalid playerId (but allow preview mode with playerId === 0)
  if (!isPreview && !isValidPlayerId(playerId)) {
    throw new Error(
      'PlayerProvider requires a valid playerId from player login.',
    );
  }

  return (
    <PlayerProviderCore
      key={`${playerId}:${isPreview}`}
      playerId={playerId}
      teamId={teamId}
      isPreview={isPreview}
    >
      {children}
    </PlayerProviderCore>
  );
}
